/**
 * AI Permission Matrix — client-side mirror of database/aiPermissions.js
 *
 * Used by React components to hide controls a user can't invoke. This is
 * polish only — the server-side enforcement at the IPC handler level is
 * what actually gates calls.
 *
 * Keep this in sync with database/aiPermissions.js. The two files MUST
 * agree on which roles can use which features. If they diverge, the user
 * sees buttons that error when clicked.
 */

export const AI_FEATURES = {
  outreach_message:           { model: 'haiku', roles: ['sales', 'physician', 'nurse', 'admin', 'sysadmin', 'manager'],
                                viewRoles: ['sales', 'physician', 'nurse', 'admin', 'sysadmin', 'manager', 'concierge', 'staff'] },
  patient_intent_score:       { model: 'haiku', roles: ['sales', 'physician', 'nurse', 'admin', 'sysadmin', 'manager'],
                                viewRoles: ['sales', 'physician', 'nurse', 'admin', 'sysadmin', 'manager', 'concierge'] },
  no_show_risk_score:         { model: 'haiku', roles: ['sales', 'physician', 'nurse', 'admin', 'sysadmin', 'manager', 'concierge'],
                                viewRoles: ['sales', 'physician', 'nurse', 'admin', 'sysadmin', 'manager', 'concierge'] },
  call_summary:               { model: 'haiku', roles: ['sales', 'physician', 'nurse', 'admin', 'sysadmin', 'manager'],
                                viewRoles: ['sales', 'physician', 'nurse', 'admin', 'sysadmin', 'manager'] },
  protocol_match:             { model: 'opus',  roles: ['physician', 'admin', 'sysadmin'],
                                viewRoles: ['physician', 'nurse', 'admin', 'sysadmin'] },
  differential_dx:            { model: 'opus',  roles: ['physician', 'admin', 'sysadmin'],
                                viewRoles: ['physician', 'nurse', 'admin', 'sysadmin'] },
  lab_summary:                { model: 'opus',  roles: ['physician', 'nurse', 'admin', 'sysadmin'],
                                viewRoles: ['physician', 'nurse', 'admin', 'sysadmin'] },
  drug_interaction:           { model: 'opus',  roles: ['physician', 'nurse', 'admin', 'sysadmin'],
                                viewRoles: ['physician', 'nurse', 'admin', 'sysadmin'] },
  research_cohort_outcomes:   { model: 'opus',  roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'admin', 'sysadmin'] },
  research_literature_mining: { model: 'opus',  roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'admin', 'sysadmin'] },
  research_biomarker_correlation: { model: 'opus', roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'admin', 'sysadmin'] },
  // Phase F.3 integrations (Biolab ↔ Research ↔ Patient bridges)
  research_cohort_import:     { model: null,   roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'admin', 'sysadmin'] },
  biolab_research_link:       { model: null,   roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'nurse', 'admin', 'sysadmin'] },
  biolab_clinical_ai:         { model: 'opus', roles: ['physician', 'admin', 'sysadmin'],
                                viewRoles: ['physician', 'nurse', 'admin', 'sysadmin'] },
  research_protocol_publish:  { model: null,   roles: ['physician', 'admin', 'sysadmin'],
                                viewRoles: ['physician', 'nurse', 'admin', 'sysadmin'] },
  research_protocol_library:  { model: null,   roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'nurse', 'admin', 'sysadmin'] },
  research_protocol_synthesis: { model: 'opus', roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'nurse', 'admin', 'sysadmin'] },
  // Phase F.3.4 — Global Research Source Federation + Proprietary Vault
  research_source_search:     { model: null, roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'nurse', 'admin', 'sysadmin'] },
  research_source_admin:      { model: null, roles: ['admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'admin', 'sysadmin'] },
  research_document_translate: { model: null, roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'admin', 'sysadmin'] },
  proprietary_vault_view:     { model: null, roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'admin', 'sysadmin'] },
  proprietary_vault_submit:   { model: null, roles: ['researcher', 'physician', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'admin', 'sysadmin'] },
  proprietary_vault_validate_doctor:   { model: null, roles: ['physician', 'admin', 'sysadmin'],
                                viewRoles: ['physician', 'researcher', 'admin', 'sysadmin'] },
  proprietary_vault_validate_research: { model: null, roles: ['researcher', 'admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'admin', 'sysadmin'] },
  proprietary_vault_publish:  { model: null, roles: ['admin', 'sysadmin'],
                                viewRoles: ['researcher', 'physician', 'admin', 'sysadmin'] },
  // Phase G: Pipeline Intelligence
  pipeline_health_metrics:    { model: null,    roles: ['sales', 'admin', 'sysadmin', 'manager', 'board_of_directors', 'physician'],
                                viewRoles: ['sales', 'admin', 'sysadmin', 'manager', 'board_of_directors', 'physician'] },
  deal_health_score:          { model: 'haiku', roles: ['sales', 'admin', 'sysadmin', 'manager'],
                                viewRoles: ['sales', 'admin', 'sysadmin', 'manager', 'board_of_directors'] },
  pipeline_advance_suggestions: { model: 'haiku', roles: ['sales', 'admin', 'sysadmin', 'manager'],
                                viewRoles: ['sales', 'admin', 'sysadmin', 'manager'] },
  pipeline_forecast:          { model: null,    roles: ['admin', 'sysadmin', 'manager', 'board_of_directors'],
                                viewRoles: ['sales', 'admin', 'sysadmin', 'manager', 'board_of_directors'] },
  next_best_action:           { model: 'haiku', roles: ['sales', 'physician', 'nurse', 'admin', 'sysadmin', 'manager', 'concierge'],
                                viewRoles: ['sales', 'physician', 'nurse', 'admin', 'sysadmin', 'manager', 'concierge'] },
  pipeline_copilot:           { model: 'opus',  roles: ['admin', 'sysadmin', 'manager'],
                                viewRoles: ['admin', 'sysadmin', 'manager'] },
  dashboard_sales_intelligence:    { model: null, roles: ['sales', 'admin', 'sysadmin', 'manager', 'board_of_directors'],
                                viewRoles: ['sales', 'admin', 'sysadmin', 'manager', 'board_of_directors'] },
  dashboard_operations_intelligence: { model: null, roles: ['concierge', 'physician', 'nurse', 'admin', 'sysadmin', 'manager', 'board_of_directors'],
                                viewRoles: ['concierge', 'physician', 'nurse', 'admin', 'sysadmin', 'manager', 'board_of_directors'] },
  dashboard_marketing_intelligence: { model: null, roles: ['admin', 'sysadmin', 'manager', 'board_of_directors'],
                                viewRoles: ['admin', 'sysadmin', 'manager', 'board_of_directors'] },
  dashboard_clinical_intelligence:  { model: null, roles: ['physician', 'nurse', 'admin', 'sysadmin'],
                                viewRoles: ['physician', 'nurse', 'admin', 'sysadmin'] },
};

export function canUse(role, featureId) {
  const feat = AI_FEATURES[featureId];
  if (!feat || !role) return false;
  return Array.isArray(feat.roles) && feat.roles.includes(role);
}

export function canView(role, featureId) {
  const feat = AI_FEATURES[featureId];
  if (!feat || !role) return false;
  if (Array.isArray(feat.roles) && feat.roles.includes(role)) return true;
  return Array.isArray(feat.viewRoles) && feat.viewRoles.includes(role);
}

export function modelFor(featureId) {
  return AI_FEATURES[featureId]?.model ?? null;
}
